<template>
  <div class="exception-date-grid" :class="{ 'grid-hourly-mode': showHourly }">
    <div class="grid-scroll-wrapper" :style="{ maxHeight: maxHeight }">
      <!-- Column Headers (Dates, optionally with hourly sub-columns) -->
      <div class="grid-header">
        <div class="grid-corner">
          <span class="small text-muted">{{ cornerLabel }}</span>
          <button v-if="locationSort !== undefined" class="btn btn-link btn-sm p-0 ms-1 text-muted sort-location-btn" @click.stop="$emit('sort-location')" title="Sort locations">
            <i class="bi" :class="locationSort === 'asc' ? 'bi-sort-alpha-down' : locationSort === 'desc' ? 'bi-sort-alpha-up-alt' : 'bi-filter'"></i>
          </button>
        </div>
        <div class="grid-header-cells">
          <template v-if="showHourly">
            <div 
              v-for="date in dates" 
              :key="date"
              class="grid-date-group"
              :class="{ 'today': isToday(date), 'weekend': isWeekend(date) }"
            >
              <div class="date-group-label">
                <span class="date-dow">{{ formatDow(date) }}</span>
                <span class="date-day">{{ formatDay(date) }}</span>
                <span class="date-month">{{ formatMonth(date) }}</span>
              </div>
              <div class="hour-headers">
                <div 
                  v-for="h in displayHours" :key="h"
                  class="grid-hour-header"
                >{{ formatHourShort(h) }}</div>
              </div>
            </div>
          </template>
          <template v-else>
            <div 
              v-for="date in dates" 
              :key="date"
              class="grid-date-header"
              :class="{ 'today': isToday(date), 'weekend': isWeekend(date) }"
            >
              <div class="date-dow">{{ formatDow(date) }}</div>
              <div class="date-day">{{ formatDay(date) }}</div>
            </div>
          </template>
        </div>
      </div>

      <!-- Location Rows -->
      <div class="grid-body">
        <div v-for="result in filteredResults" :key="result.location" class="grid-location-group" :data-location-id="result.id || result.location">
          <!-- Main Location Row -->
          <div class="grid-row" :class="{ 'grid-row-expandable': expandedLocations || activeCriteria.length > 0 }" @click="handleRowClick(result)">
            <!-- Location Name -->
            <div class="grid-location" :title="result.name ? (result.address || result.resolvedAddress || result.location) : ''">
              <i v-if="expandedLocations || activeCriteria.length > 0" class="bi me-1" :class="isResultExpanded(result) ? 'bi-chevron-down' : 'bi-chevron-right'" style="font-size: 0.7rem; width: 10px; text-align: center;"></i>
              <div class="location-name">{{ result.name || result.location }}</div>
              <div v-if="result.status === 'error'" class="location-error">
                <i class="bi bi-exclamation-circle text-danger"></i>
              </div>
            </div>

            <!-- Day Cells (or hourly cells) -->
            <div class="grid-row-cells">
              <template v-if="showHourly">
                <div v-for="date in dates" :key="'h-' + date" class="grid-day-cells">
                  <div 
                    v-for="h in displayHours" :key="date + '-' + h"
                    class="grid-cell grid-cell-hourly"
                    :class="isResultExpanded(result) || ganttMode ? '' : hourlySummaryCellCache[(result.id || result.location) + '|' + date + '|' + h] || ''"
                    @mouseenter="$event.target.title = isResultExpanded(result) ? '' : getHourlySummaryTooltip(result, date, h)"
                    @click.stop="handleCellClick(result, date)"
                  >
                    <template v-if="result.status === 'ok' && !isResultExpanded(result)">
                      <div class="cell-content">
                        <!-- Gantt mode: thin line per criterion (colored if flagged, transparent if not) -->
                        <template v-if="ganttMode && activeCriteria.length > 0">
                          <div class="cell-mini-gantt">
                            <div
                              v-for="c in activeCriteria" :key="c.id"
                              class="mini-gantt-row"
                              :class="isHourCriterionFlagged(result, date, h, c) ? ('mini-gantt-bar-' + (c.color || 'warning')) : 'mini-gantt-bar-none'"
                              :title="criterionLabel(c)"
                            ></div>
                          </div>
                        </template>
                        <!-- Binary severity, non-gantt: only flagged bars -->
                        <template v-else-if="binarySeverity">
                          <div v-if="getDayData(result, date)" class="cell-mini-gantt">
                            <template v-for="c in activeCriteria" :key="c.id">
                              <div
                                v-if="isHourCriterionFlagged(result, date, h, c)"
                                class="mini-gantt-row"
                                :class="'mini-gantt-bar-' + (c.color || 'warning')"
                                :title="criterionLabel(c)"
                              ></div>
                            </template>
                          </div>
                        </template>
                      </div>
                    </template>
                  </div>
                </div>
              </template>
              <template v-else>
                <div 
                  v-for="date in dates" 
                  :key="date"
                  class="grid-cell"
                  :class="isResultExpanded(result) ? '' : getCellClass(result, date)"
                  :title="isResultExpanded(result) ? '' : getCellTooltip(result, date)"
                  @click.stop="handleCellClick(result, date)"
                >
                  <template v-if="!isResultExpanded(result)">
                  <template v-if="result.status === 'ok'">
                    <div v-if="getDayData(result, date)" class="cell-content">
                      <template v-if="ganttMode && activeCriteria.length > 0">
                        <div class="cell-mini-gantt">
                          <div
                            v-for="c in activeCriteria" :key="c.id"
                            class="mini-gantt-row"
                            :class="isDayCriterionFlagged(result, date, c) ? ('mini-gantt-bar-' + (c.color || 'warning')) : 'mini-gantt-bar-none'"
                            :title="criterionLabel(c)"
                          ></div>
                        </div>
                      </template>
                      <template v-else>
                        <div class="cell-value cell-value-sm">{{ formatCellValue(result, date) }}</div>
                        <template v-if="showSecondary && getSecondaryValue(result, date)">
                          <div class="cell-secondary">{{ getSecondaryValue(result, date) }}</div>
                        </template>
                      </template>
                      <!-- Flagged indicator bars (binary severity mode, non-gantt) -->
                      <div v-if="!ganttMode && binarySeverity && getDayFlaggedCriteria(result, date).length > 0" class="cell-mini-gantt">
                        <div
                          v-for="c in getDayFlaggedCriteria(result, date)" :key="c.id"
                          class="mini-gantt-row"
                          :class="'mini-gantt-bar-' + (c.color || 'warning')"
                          :title="criterionLabel(c)"
                        ></div>
                      </div>
                      <div v-if="!ganttMode && !binarySeverity && getDayData(result, date)?.isException" class="cell-indicator">
                        <i class="bi bi-exclamation-triangle"></i>
                      </div>
                    </div>
                    <div v-else class="cell-empty"></div>
                  </template>
                  <template v-else-if="result.status === 'pending'">
                    <div class="cell-loading">
                      <span class="spinner-border spinner-border-sm"></span>
                    </div>
                  </template>
                  <template v-else>
                    <div class="cell-error">!</div>
                  </template>
                  </template>
                </div>
              </template>
            </div>
          </div>

          <!-- Element Expansion Rows (per-location expand or legacy global) -->
          <template v-if="isResultExpanded(result) && result.status === 'ok' && displayCriteria.length > 0">
            <div
              v-for="criterion in displayCriteria" :key="result.location + '-' + criterion.id"
              class="grid-row grid-row-element"
              :class="{ 'grid-row-gantt': ganttMode }"
            >
              <div class="grid-location grid-location-element" :title="criterionLabel(criterion)">
                <div class="location-name">
                  <span class="criterion-dot" :class="'dot-' + (criterion.color || 'warning')"></span>
                  {{ criterionLabel(criterion) }}
                </div>
              </div>
              <div class="grid-row-cells" :class="{ 'gantt-row-cells': ganttMode }">
                <!-- Gantt bar mode (skip if hourly is active — hourly takes priority) -->
                <template v-if="ganttMode && !showHourly">
                  <div
                    v-for="(date, dateIdx) in dates" :key="date"
                    class="grid-cell gantt-cell"
                    :class="getGanttCellClass(result, date, dateIdx, criterion)"
                    :title="getElementCellTooltip(result, date, criterion)"
                    @click.stop="handleCellClick(result, date)"
                  >
                    <!-- Bar start: colored bar spans multiple cells (label omitted — criterion name is in left column) -->
                    <div v-if="isGanttBarStart(result, dateIdx, criterion)" class="gantt-bar" :class="'gantt-bar-' + (criterion.color || 'warning')" :style="getGanttBarStyle(result, dateIdx, criterion)">
                    </div>
                    <!-- Value: shown inside bar (white) or outside (muted) -->
                    <span v-if="isInGanttBar(result, dateIdx, criterion) && getElementCellValue(result, date, criterion)" class="gantt-cell-value gantt-cell-value-bar" :class="'gantt-value-' + (criterion.color || 'warning')">{{ getElementCellValue(result, date, criterion) }}</span>
                    <span v-else-if="getElementCellValue(result, date, criterion)" class="gantt-cell-value gantt-cell-value-muted">{{ getElementCellValue(result, date, criterion) }}</span>
                  </div>
                </template>
                <!-- Hourly element cells -->
                <template v-else-if="showHourly">
                  <div v-for="date in dates" :key="'eh-' + date" class="grid-day-cells">
                    <div
                      v-for="h in displayHours" :key="date + '-' + h"
                      class="grid-cell grid-cell-hourly"
                      :class="[
                        (hourlyCellCache[(result.id || result.location) + '|' + criterion.id + '|' + date + '|' + h] || {}).cls || '',
                        { 'hourly-bar-start': (hourlyCellCache[(result.id || result.location) + '|' + criterion.id + '|' + date + '|' + h] || {}).barStart,
                          'hourly-bar-end': (hourlyCellCache[(result.id || result.location) + '|' + criterion.id + '|' + date + '|' + h] || {}).barEnd }
                      ]"
                      @mouseenter="$event.target.title = getElementHourlyCellTooltip(result, date, h, criterion)"
                      @click.stop="handleCellClick(result, date)"
                    >
                      <span class="gantt-cell-value" :class="(hourlyCellCache[(result.id || result.location) + '|' + criterion.id + '|' + date + '|' + h] || {}).cls ? 'gantt-value-' + (criterion.color || 'warning') : 'gantt-cell-value-muted'">{{ (hourlyCellCache[(result.id || result.location) + '|' + criterion.id + '|' + date + '|' + h] || {}).val || '-' }}</span>
                    </div>
                  </div>
                </template>
                <!-- Daily element cells -->
                <template v-else>
                  <div
                    v-for="date in dates" :key="date"
                    class="grid-cell"
                    :class="getElementCellClass(result, date, criterion)"
                    :title="getElementCellTooltip(result, date, criterion)"
                    @click.stop="handleCellClick(result, date)"
                  >
                    <div class="cell-content">
                      <div class="cell-value cell-value-sm">{{ getElementCellValue(result, date, criterion) }}</div>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </template>
        </div>

        <!-- Empty State -->
        <div v-if="filteredResults.length === 0" class="grid-empty">
          <i class="bi bi-inbox fs-3 text-muted mb-2"></i>
          <p class="text-muted mb-0">No results to display</p>
        </div>
      </div>
    </div>

    <!-- Legend -->
    <div v-if="showLegend" class="grid-legend">
      <template v-if="binarySeverity">
        <div class="legend-item">
          <span class="legend-swatch legend-clear"></span>
          <span>Clear</span>
        </div>
        <div class="legend-item">
          <span class="legend-swatch legend-flagged"></span>
          <span>Flagged</span>
        </div>
      </template>
      <template v-else>
        <div class="legend-item">
          <span class="legend-swatch legend-ok"></span>
          <span>{{ okLabel }}</span>
        </div>
        <div class="legend-item">
          <span class="legend-swatch legend-warning"></span>
          <span>{{ warningLabel }}</span>
        </div>
        <div class="legend-item">
          <span class="legend-swatch legend-danger"></span>
          <span>{{ dangerLabel }}</span>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { WEATHER_FIELDS } from '../exceptionCriteriaPresets.js';

export default {
  name: 'ExceptionDateGrid',

  props: {
    results: {
      type: Array,
      default: () => []
    },
    dates: {
      type: Array,
      default: () => []
    },
    primaryField: {
      type: String,
      default: 'tempmax'
    },
    secondaryField: {
      type: String,
      default: null
    },
    showSecondary: {
      type: Boolean,
      default: false
    },
    showLegend: {
      type: Boolean,
      default: true
    },
    filter: {
      type: String,
      default: 'all'
    },
    maxHeight: {
      type: String,
      default: '400px'
    },
    cornerLabel: {
      type: String,
      default: 'Location'
    },
    okLabel: {
      type: String,
      default: 'OK'
    },
    warningLabel: {
      type: String,
      default: 'Warning'
    },
    dangerLabel: {
      type: String,
      default: 'Critical'
    },
    unitGroup: {
      type: String,
      default: 'metric'
    },
    /** When true, show sub-rows for each active criterion (legacy global toggle) */
    expandByElement: {
      type: Boolean,
      default: false
    },
    /** Active criteria for element expansion */
    activeCriteria: {
      type: Array,
      default: () => []
    },
    /** Custom severity levels configuration */
    severityLevels: {
      type: Object,
      default: null
    },
    /** Per-location expanded IDs (overrides expandByElement when provided) */
    expandedLocations: {
      type: Array,
      default: null
    },
    /** When true, use binary severity (Clear/Flagged). Clear cells get white bg. */
    binarySeverity: {
      type: Boolean,
      default: false
    },
    /** Show hourly sub-columns for each date */
    showHourly: {
      type: Boolean,
      default: false
    },
    /** Business hours config { enabled, start: 'HH:MM', end: 'HH:MM' } */
    businessHours: {
      type: Object,
      default: null
    },
    /** Filter to show only items flagged by a specific criterion ID */
    criteriaFilter: {
      type: String,
      default: null
    },
    /** When true, summary row shows only criteria fields and expansion uses Gantt bars */
    ganttMode: {
      type: Boolean,
      default: false
    },
    /** Current sort direction for the location column */
    locationSort: {
      type: String,
      default: undefined
    }
  },

  emits: ['cell-click', 'toggle-expand', 'sort-location'],

  computed: {
    /** Hours to display for each date when in hourly mode */
    displayHours() {
      if (!this.showHourly) return [];
      let hours = [];
      for (let h = 0; h < 24; h++) { hours.push(h); }
      if (this.businessHours?.enabled) {
        const startH = parseInt(this.businessHours.start?.split(':')[0] || '0');
        const endH = parseInt(this.businessHours.end?.split(':')[0] || '24');
        hours = hours.filter(h => h >= startH && h < endH);
      }
      return hours;
    },

    /** Criteria to display in expansion — always show all criteria.
     *  criteriaFilter only filters which locations are shown, not which
     *  criteria rows appear within those locations. */
    displayCriteria() {
      return this.activeCriteria;
    },

    /** Unique fields used across all active criteria (for ganttMode summary row) */
    criteriaFields() {
      if (!this.ganttMode) return [];
      const seen = new Set();
      const fields = [];
      for (const c of this.activeCriteria) {
        if (c.field && !seen.has(c.field)) {
          seen.add(c.field);
          fields.push(c.field);
        }
      }
      return fields;
    },

    /** Pre-compute Gantt bar segments for each criterion per result.
     *  Returns Map<resultId, Map<criterionId, Array<{startIdx, endIdx}>>> */
    ganttBarMap() {
      if (!this.ganttMode || this.showHourly) return {};
      const map = {};
      for (const result of this.filteredResults) {
        if (result.status !== 'ok') continue;
        const cMap = {};
        for (const criterion of this.displayCriteria) {
          // Alert criteria: use actual onset/ends dates for exact bar spans
          if (criterion.field === 'alerts' && result.alertsData?.length > 0) {
            const segments = [];
            for (const alert of result.alertsData) {
              let startIdx = 0;
              let endIdx = this.dates.length - 1;
              if (alert.onset) {
                const onsetDate = alert.onset.split('T')[0];
                const idx = this.dates.indexOf(onsetDate);
                if (idx >= 0) startIdx = idx;
                else if (onsetDate > this.dates[this.dates.length - 1]) continue;
              }
              if (alert.ends) {
                const endsDate = alert.ends.split('T')[0];
                const idx = this.dates.indexOf(endsDate);
                if (idx >= 0) endIdx = idx;
                else if (endsDate < this.dates[0]) continue;
              }
              if (startIdx <= endIdx) {
                segments.push({ startIdx, endIdx, label: alert.event || 'Weather Alert' });
              }
            }
            cMap[criterion.id] = segments;
          } else {
            // Standard criteria: contiguous segments from per-day matchedCriteria
            const segments = [];
            let segStart = null;
            for (let i = 0; i < this.dates.length; i++) {
              const day = this.getDayData(result, this.dates[i]);
              const matched = day?.matchedCriteria?.includes(criterion.id);
              if (matched) {
                if (segStart === null) segStart = i;
              } else {
                if (segStart !== null) {
                  segments.push({ startIdx: segStart, endIdx: i - 1 });
                  segStart = null;
                }
              }
            }
            if (segStart !== null) {
              segments.push({ startIdx: segStart, endIdx: this.dates.length - 1 });
            }
            cMap[criterion.id] = segments;
          }
        }
        map[result.id] = cMap;
      }
      return map;
    },

    filteredResults() {
      let results = this.results;

      // Status filter
      if (this.filter === 'ok') {
        results = results.filter(r => r.maxSeverity === 'ok');
      } else if (this.filter === 'flagged') {
        results = results.filter(r => r.maxSeverity === 'warning' || r.maxSeverity === 'danger');
      } else if (this.filter === 'warning') {
        results = results.filter(r => r.maxSeverity === 'warning');
      } else if (this.filter === 'danger') {
        results = results.filter(r => r.maxSeverity === 'danger');
      }

      // Criteria filter is handled by the parent component which passes
      // pre-filtered results. No additional filtering needed here.

      return results;
    },

    /** Check if a result is expanded (per-location or global) */
    isExpandedMap() {
      const map = {};
      if (this.expandedLocations) {
        for (const id of this.expandedLocations) { map[id] = true; }
      }
      return map;
    },

    /** Pre-built Map for O(1) day lookups: key = resultId + '|' + date */
    dayDataMap() {
      const map = {};
      for (const result of this.results) {
        if (!result.days) continue;
        const rid = result.id || result.location;
        for (const day of result.days) {
          map[rid + '|' + day.datetime] = day;
        }
      }
      return map;
    },

    /** Pre-built Map for O(1) hour lookups: key = resultId + '|' + date + '|' + hourNum */
    hourDataMap() {
      if (!this.showHourly) return {};
      const map = {};
      for (const result of this.results) {
        if (!result.days) continue;
        const rid = result.id || result.location;
        for (const day of result.days) {
          if (!day.hours) continue;
          for (const hour of day.hours) {
            // hour.datetime is 'HH:00:00', extract hour number
            const h = parseInt(hour.datetime.substring(0, 2));
            map[rid + '|' + day.datetime + '|' + h] = hour;
          }
        }
      }
      return map;
    },

    /** Pre-built Map for O(1) criterion lookups by ID */
    activeCriteriaById() {
      const map = {};
      for (const c of this.activeCriteria) {
        map[c.id] = c;
      }
      return map;
    },

    /** Pre-computed hourly cell data for expanded results.
     *  key = resultId + '|' + criterionId + '|' + date + '|' + hour
     *  value = { cls: String, val: String }
     *  Only built for expanded results in hourly mode. */
    hourlyCellCache() {
      if (!this.showHourly) return {};
      const cache = {};
      const hours = this.displayHours;
      for (const result of this.filteredResults) {
        const rid = result.id || result.location;
        if (!this.isExpandedMap[rid] || result.status !== 'ok') continue;
        for (const criterion of this.displayCriteria) {
          const cid = criterion.id;
          const color = criterion.color || 'warning';
          const field = (criterion.field === 'tempmax' || criterion.field === 'tempmin') ? 'temp' : criterion.field;
          for (const date of this.dates) {
            const day = this.dayDataMap[rid + '|' + date];
            const hourCriteriaMap = day?.hourCriteriaMap;
            for (const h of hours) {
              const hour = this.hourDataMap[rid + '|' + date + '|' + h];
              const hourStr = String(h).padStart(2, '0') + ':00:00';
              const matched = hourCriteriaMap?.[hourStr]?.includes(cid);

              // value
              let val = '-';
              if (field === 'alerts') {
                // For alerts, count how many alerts are active for this hour
                const result = this.filteredResults.find(r => (r.id || r.location) === rid);
                const alertsData = result?.alertsData || [];
                if (alertsData.length > 0) {
                  const dayDate = date;
                  const hStart = new Date(dayDate + 'T' + hourStr);
                  const hEnd = new Date(dayDate + 'T' + String(h).padStart(2, '0') + ':59:59');
                  let alertCount = 0;
                  for (const alert of alertsData) {
                    const onset = alert.onset ? new Date(alert.onset) : null;
                    const ends = alert.ends ? new Date(alert.ends) : null;
                    if ((!onset || onset <= hEnd) && (!ends || ends >= hStart)) alertCount++;
                  }
                  val = String(alertCount);
                } else {
                  val = '0';
                }
              } else if (hour) {
                const raw = hour[field];
                if (raw !== undefined && raw !== null) {
                  if (['temp', 'tempmax', 'tempmin'].includes(field)) val = Math.round(raw) + '°';
                  else if (field === 'precipprob') val = Math.round(raw) + '%';
                  else if (['windspeed', 'windgust'].includes(field)) val = String(Math.round(raw));
                  else if (['precip', 'snow', 'snowdepth'].includes(field)) val = raw > 0 ? raw.toFixed(1) : '0';
                  else val = String(raw);
                } 
              }

              // class
              let cls = '';
              if (matched) {
                if (color === 'danger') cls = 'cell-danger';
                else if (color === 'info') cls = 'cell-info';
                else if (color === 'purple') cls = 'cell-purple';
                else if (color === 'orange') cls = 'cell-orange';
                else if (color === 'teal') cls = 'cell-teal';
                else if (color === 'pink') cls = 'cell-pink';
                else cls = 'cell-warning';
              }

              cache[rid + '|' + cid + '|' + date + '|' + h] = { cls, val };
            }
          }
        }
      }
      // Second pass: mark bar-start / bar-end for rounded corners
      for (const result of this.filteredResults) {
        const rid = result.id || result.location;
        if (!this.isExpandedMap[rid] || result.status !== 'ok') continue;
        for (const criterion of this.displayCriteria) {
          const cid = criterion.id;
          // Build flat list of keys in order across all dates+hours
          const keys = [];
          for (const date of this.dates) {
            for (const h of hours) {
              keys.push(rid + '|' + cid + '|' + date + '|' + h);
            }
          }
          for (let i = 0; i < keys.length; i++) {
            const entry = cache[keys[i]];
            if (!entry || !entry.cls) continue;
            const prevTriggered = i > 0 && cache[keys[i - 1]]?.cls;
            const nextTriggered = i < keys.length - 1 && cache[keys[i + 1]]?.cls;
            if (!prevTriggered) entry.barStart = true;
            if (!nextTriggered) entry.barEnd = true;
          }
        }
      }
      return cache;
    },

    /** Pre-computed summary cell classes for the main hourly row (non-expanded).
     *  key = resultId + '|' + date + '|' + hour, value = class array */
    hourlySummaryCellCache() {
      if (!this.showHourly) return {};
      const cache = {};
      for (const result of this.filteredResults) {
        const rid = result.id || result.location;
        if (this.isExpandedMap[rid] || result.status !== 'ok') continue;
        for (const date of this.dates) {
          const day = this.dayDataMap[rid + '|' + date];
          if (!day) continue;
          const hourCriteriaMap = day.hourCriteriaMap;
          for (const h of this.displayHours) {
            const hourStr = String(h).padStart(2, '0') + ':00:00';
            const hourCriteria = hourCriteriaMap?.[hourStr] || [];
            let cls = '';
            if (hourCriteria.length > 0) {
              if (this.binarySeverity) {
                cls = 'cell-flagged';
              } else {
                cls = 'cell-warning';
                for (const cid of hourCriteria) {
                  const c = this.activeCriteriaById[cid];
                  if (c) {
                    if (c.color === 'danger') { cls = 'cell-danger'; break; }
                    if (c.color === 'info') { cls = 'cell-info'; break; }
                    if (c.color === 'purple') { cls = 'cell-purple'; break; }
                    if (c.color === 'orange') { cls = 'cell-orange'; break; }
                    if (c.color === 'teal') { cls = 'cell-teal'; break; }
                    if (c.color === 'pink') { cls = 'cell-pink'; break; }
                  }
                }
              }
            } else if (!this.binarySeverity) {
              cls = 'cell-ok';
            }
            cache[rid + '|' + date + '|' + h] = cls;
          }
        }
      }
      return cache;
    }
  },

  methods: {
    /** Get a human-readable label for a criterion, including granularity prefix for dual fields */
    criterionLabel(c) {
      if (c.name) return c.name;
      const fieldInfo = WEATHER_FIELDS[c.field];
      if (!fieldInfo) return c.field;
      const gran = c.granularity || 'daily';
      const prefix = (fieldInfo.section === 'both') ? (gran === 'hourly' ? 'Hourly ' : 'Daily ') : '';
      return prefix + fieldInfo.label;
    },

    getDayData(result, date) {
      const rid = result.id || result.location;
      return this.dayDataMap[rid + '|' + date] || null;
    },

    getCellClass(result, date) {
      const classes = [];
      
      if (result.status !== 'ok') {
        classes.push('cell-status-' + result.status);
        return classes;
      }

      const day = this.getDayData(result, date);
      if (!day) return classes;

      if (this.binarySeverity) {
        // Binary mode: white for clear, light tint for flagged
        if (day.isException) {
          classes.push('cell-flagged');
        }
        // No class = white background
      } else {
        // Legacy 3-level mode
        if (day.severity === 'danger') {
          classes.push('cell-danger');
        } else if (day.severity === 'warning') {
          classes.push('cell-warning');
        } else {
          classes.push('cell-ok');
        }

        if (day.isException) {
          classes.push('cell-exception');
        }
      }

      return classes;
    },

    getCellTooltip(result, date) {
      if (result.status !== 'ok') return result.errorMessage || 'Error';

      const day = this.getDayData(result, date);
      if (!day) return 'No data';

      const parts = [date];
      
      if (day.tempmax !== undefined) {
        const unit = this.unitGroup === 'us' ? '°F' : '°C';
        parts.push(`High: ${Math.round(day.tempmax)}${unit}`);
      }
      if (day.tempmin !== undefined) {
        const unit = this.unitGroup === 'us' ? '°F' : '°C';
        parts.push(`Low: ${Math.round(day.tempmin)}${unit}`);
      }
      if (day.precipprob !== undefined) {
        parts.push(`Precip: ${Math.round(day.precipprob)}%`);
      }
      if (day.windgust !== undefined) {
        const unit = this.unitGroup === 'us' ? 'mph' : 'km/h';
        parts.push(`Wind: ${Math.round(day.windgust)}${unit}`);
      }

      if (day.matchedCriteria?.length > 0) {
        parts.push(`⚠️ ${day.matchedCriteria.length} issue(s)`);
      }

      return parts.join('\n');
    },

    formatCellValue(result, date) {
      const day = this.getDayData(result, date);
      if (!day) return '-';

      const value = day[this.primaryField];
      if (value === undefined || value === null) return '-';

      // Format based on field type
      if (['tempmax', 'tempmin', 'temp'].includes(this.primaryField)) {
        return Math.round(value) + '°';
      }
      if (['precipprob'].includes(this.primaryField)) {
        return Math.round(value) + '%';
      }
      if (['windspeed', 'windgust'].includes(this.primaryField)) {
        return Math.round(value);
      }
      if (['precip', 'snow'].includes(this.primaryField)) {
        return value > 0 ? value.toFixed(1) : '0';
      }

      return value;
    },

    getSecondaryValue(result, date) {
      if (!this.secondaryField) return null;

      const day = this.getDayData(result, date);
      if (!day) return null;

      const value = day[this.secondaryField];
      if (value === undefined || value === null) return null;

      // Format based on field type
      if (['tempmax', 'tempmin', 'temp'].includes(this.secondaryField)) {
        return Math.round(value) + '°';
      }
      if (['precipprob'].includes(this.secondaryField)) {
        return Math.round(value) + '%';
      }
      if (['windspeed', 'windgust'].includes(this.secondaryField)) {
        return Math.round(value);
      }

      return value;
    },

    formatDow(date) {
      const d = new Date(date + 'T12:00:00');
      return d.toLocaleDateString('en-US', { weekday: 'short' });
    },

    formatDay(date) {
      const parts = date.split('-');
      return parseInt(parts[2]);
    },

    formatMonth(date) {
      const d = new Date(date + 'T12:00:00');
      return d.toLocaleDateString('en-US', { month: 'short' });
    },

    isToday(date) {
      const today = new Date().toISOString().split('T')[0];
      return date === today;
    },

    isWeekend(date) {
      const d = new Date(date + 'T12:00:00');
      const dow = d.getDay();
      return dow === 0 || dow === 6;
    },

    handleCellClick(result, date) {
      const day = this.getDayData(result, date);
      this.$emit('cell-click', { result, date, day });
    },

    // ============================================
    // Per-location Expand
    // ============================================

    /** Check if a result is expanded (per-location or legacy global) */
    isResultExpanded(result) {
      if (this.expandedLocations) {
        return this.isExpandedMap[result.id] || false;
      }
      return this.expandByElement;
    },

    /** Handle row click - emit toggle-expand or fallback to cell-click */
    handleRowClick(result) {
      if (this.expandedLocations || this.activeCriteria.length > 0) {
        this.$emit('toggle-expand', result);
      }
    },

    // ============================================
    // Flagged Criteria Helpers
    // ============================================

    /** Get the color associated with a criteria field (first matching criterion's color) */
    getCriteriaFieldColor(field) {
      const c = this.activeCriteria.find(cr => cr.field === field);
      return c?.color || 'secondary';
    },

    /** Check if a specific criterion flagged a specific day */
    isDayCriterionFlagged(result, date, criterion) {
      const day = this.getDayData(result, date);
      return day?.matchedCriteria?.includes(criterion.id) || false;
    },

    /** Get the criteria that flagged a specific day */
    getDayFlaggedCriteria(result, date) {
      const day = this.getDayData(result, date);
      if (!day || !day.matchedCriteria?.length) return [];
      return day.matchedCriteria
        .map(cid => this.activeCriteriaById[cid])
        .filter(Boolean)
        .slice(0, 4); // max 4 dots
    },

    // ============================================
    // Hourly Helpers
    // ============================================

    /** Format hour number to short label (0→12a, 13→1p) */
    formatHourShort(h) {
      if (h === 0) return '12a';
      if (h === 12) return '12p';
      return h > 12 ? (h - 12) + 'p' : h + 'a';
    },

    /** Get hour data object from a result's day */
    getHourData(result, date, hourNum) {
      const rid = result.id || result.location;
      return this.hourDataMap[rid + '|' + date + '|' + hourNum] || null;
    },

    /** Get tooltip for hourly summary cell */
    getHourlySummaryTooltip(result, date, hourNum) {
      const hour = this.getHourData(result, date, hourNum);
      if (!hour) return 'No data';
      const parts = [this.formatHourShort(hourNum)];
      if (hour.temp !== undefined) {
        const unit = this.unitGroup === 'us' ? '°F' : '°C';
        parts.push(`Temp: ${Math.round(hour.temp)}${unit}`);
      }
      if (hour.precipprob !== undefined) parts.push(`Precip: ${Math.round(hour.precipprob)}%`);
      if (hour.windgust !== undefined) {
        const unit = this.unitGroup === 'us' ? 'mph' : 'km/h';
        parts.push(`Wind: ${Math.round(hour.windgust)}${unit}`);
      }
      const flagged = this.getHourFlaggedCriteria(result, date, hourNum);
      if (flagged.length > 0) {
        parts.push(`⚠️ ${flagged.map(c => this.criterionLabel(c)).join(', ')}`);
      }
      return parts.join('\n');
    },

    /** Get criteria that matched a specific hour — mirrors getDayFlaggedCriteria */
    getHourFlaggedCriteria(result, date, hourNum) {
      const day = this.getDayData(result, date);
      if (!day) return [];
      const hourStr = String(hourNum).padStart(2, '0') + ':00:00';
      const hourCriteria = day.hourCriteriaMap?.[hourStr] || [];
      if (hourCriteria.length === 0) return [];
      return hourCriteria
        .map(cid => this.activeCriteriaById[cid])
        .filter(Boolean)
        .slice(0, 4);
    },

    /** Check if a specific criterion flagged a specific hour (O(1) lookup, no array allocation) */
    isHourCriterionFlagged(result, date, hourNum, criterion) {
      const day = this.getDayData(result, date);
      if (!day) return false;
      const hourStr = String(hourNum).padStart(2, '0') + ':00:00';
      const hourCriteria = day.hourCriteriaMap?.[hourStr];
      return hourCriteria ? hourCriteria.includes(criterion.id) : false;
    },

    /** Get element cell value for hourly mode */
    getElementHourlyCellValue(result, date, hourNum, criterion) {
      // For alerts, count how many alerts are active for this hour
      if (criterion.field === 'alerts') {
        const alertsData = result?.alertsData || [];
        if (alertsData.length === 0) return '0';
        const hourStr = String(hourNum).padStart(2, '0') + ':00:00';
        const hStart = new Date(date + 'T' + hourStr);
        const hEnd = new Date(date + 'T' + String(hourNum).padStart(2, '0') + ':59:59');
        let count = 0;
        for (const alert of alertsData) {
          const onset = alert.onset ? new Date(alert.onset) : null;
          const ends = alert.ends ? new Date(alert.ends) : null;
          if ((!onset || onset <= hEnd) && (!ends || ends >= hStart)) count++;
        }
        return count;
      }
      const hour = this.getHourData(result, date, hourNum);
      if (!hour) return '-';
      const field = (criterion.field === 'tempmax' || criterion.field === 'tempmin') ? 'temp' : criterion.field;
      const val = hour[field];
      if (val === undefined || val === null) return '-';
      if (['temp', 'tempmax', 'tempmin'].includes(field)) return Math.round(val) + '°';
      if (field === 'precipprob') return Math.round(val) + '%';
      if (['windspeed', 'windgust'].includes(field)) return Math.round(val);
      if (['precip', 'snow', 'snowdepth'].includes(field)) return val > 0 ? val.toFixed(1) : '0';
      return val;
    },

    /** Get cell class for hourly element expansion cell */
    getElementHourlyCellClass(result, date, hourNum, criterion) {
      const day = this.getDayData(result, date);
      if (!day) return [];
      const hourStr = String(hourNum).padStart(2, '0') + ':00:00';
      const hourCriteria = day.hourCriteriaMap?.[hourStr] || [];
      if (hourCriteria.includes(criterion.id)) {
        if (criterion.color === 'danger') return ['cell-danger'];
        if (criterion.color === 'info') return ['cell-info'];
        if (criterion.color === 'purple') return ['cell-purple'];
        if (criterion.color === 'orange') return ['cell-orange'];
        if (criterion.color === 'teal') return ['cell-teal'];
        if (criterion.color === 'pink') return ['cell-pink'];
        return ['cell-warning'];
      }
      return [];
    },

    /** Tooltip for hourly element expansion cell */
    getElementHourlyCellTooltip(result, date, hourNum, criterion) {
      const hour = this.getHourData(result, date, hourNum);
      if (!hour) return 'No data';
      const field = (criterion.field === 'tempmax' || criterion.field === 'tempmin') ? 'temp' : criterion.field;
      const val = hour[field];
      const hourStr = String(hourNum).padStart(2, '0') + ':00:00';
      const day = this.getDayData(result, date);
      const matched = day?.hourCriteriaMap?.[hourStr]?.includes(criterion.id);
      const label = this.criterionLabel(criterion);
      return `${label}: ${val ?? 'N/A'}${matched ? ' ⚠ Flagged' : ''}`;
    },

    // ============================================
    // Element Expansion Helpers
    // ============================================

    /** Get the raw value from a day for a criterion's field */
    getElementCellValue(result, date, criterion) {
      const day = this.getDayData(result, date);
      if (!day) return '';
      // Alerts: show count of active alerts for this day
      if (criterion.field === 'alerts') {
        const count = day.activeAlerts?.length || 0;
        return count > 0 ? count : '0';
      }
      const val = day[criterion.field];
      if (val === undefined || val === null) return '';

      if (['tempmax', 'tempmin', 'temp'].includes(criterion.field)) return Math.round(val) + '°';
      if (['precipprob'].includes(criterion.field)) return Math.round(val) + '%';
      if (['windspeed', 'windgust'].includes(criterion.field)) return Math.round(val);
      if (['precip', 'snow', 'snowdepth'].includes(criterion.field)) return val > 0 ? val.toFixed(1) : '0';
      if (criterion.field === 'conditions') return val.length > 6 ? val.substring(0, 5) + '…' : val;
      return val;
    },

    /** Get CSS class for an element expansion cell */
    getElementCellClass(result, date, criterion) {
      const day = this.getDayData(result, date);
      if (!day) return [];
      // Check if this criterion matched for this day
      if (day.matchedCriteria?.includes(criterion.id)) {
        if (criterion.color === 'danger') return ['cell-danger'];
        if (criterion.color === 'info') return ['cell-info'];
        if (criterion.color === 'purple') return ['cell-purple'];
        if (criterion.color === 'orange') return ['cell-orange'];
        if (criterion.color === 'teal') return ['cell-teal'];
        if (criterion.color === 'pink') return ['cell-pink'];
        return ['cell-warning'];
      }
      return [];
    },

    /** Tooltip for element expansion cell */
    getElementCellTooltip(result, date, criterion) {
      const day = this.getDayData(result, date);
      if (!day) return 'No data';
      const label = this.criterionLabel(criterion);
      if (criterion.field === 'alerts') {
        if (day.hasAlerts && day.activeAlerts?.length) {
          return day.activeAlerts.map(a => a.event || 'Alert').join('\n');
        }
        return `${label}: None`;
      }
      const val = this.getElementCellValue(result, date, criterion);
      return `${label}: ${val || '–'}`;
    },

    // ============================================
    // Gantt Bar Helpers
    // ============================================

    /** Format a field value for the summary cell in ganttMode */
    formatFieldValue(day, field) {
      if (!day) return '-';
      const val = day[field];
      if (val === undefined || val === null) return '-';
      if (['tempmax', 'tempmin', 'temp'].includes(field)) return Math.round(val) + '°';
      if (field === 'precipprob') return Math.round(val) + '%';
      if (['windspeed', 'windgust'].includes(field)) return Math.round(val);
      if (['precip', 'snow', 'snowdepth'].includes(field)) return val > 0 ? val.toFixed(1) : '0';
      if (field === 'humidity') return Math.round(val) + '%';
      if (field === 'conditions') return val.length > 5 ? val.substring(0, 4) + '…' : val;
      if (field === 'alerts') return day.hasAlerts ? '⚠' : '-';
      if (field === 'severerisk') return Math.round(val);
      return val;
    },

    /** Get the display value for a mini-bar; returns empty string for missing data, ⚠ for alerts */
    getMiniBarValue(day, criterion) {
      if (!day || !criterion?.field) return '';
      const field = criterion.field;
      if (field === 'alerts') return day.hasAlerts ? '\u26a0' : '';
      const val = day[field];
      if (val === undefined || val === null) return '';
      if (['tempmax', 'tempmin', 'temp'].includes(field)) return Math.round(val) + '°';
      if (field === 'precipprob') return Math.round(val) + '%';
      if (['windspeed', 'windgust'].includes(field)) return Math.round(val);
      if (['precip', 'snow', 'snowdepth'].includes(field)) return val > 0 ? val.toFixed(1) : '0';
      if (field === 'humidity') return Math.round(val) + '%';
      if (field === 'conditions') return val.length > 5 ? val.substring(0, 4) + '…' : val;
      if (field === 'severerisk') return Math.round(val);
      return val;
    },

    /** Get CSS class for a gantt cell (subtle highlight on flagged days) */
    getGanttCellClass(result, date, dateIdx, criterion) {
      const day = this.getDayData(result, date);
      if (!day) return [];
      if (day.matchedCriteria?.includes(criterion.id)) {
        return ['gantt-cell-active'];
      }
      return [];
    },

    /** Check if a gantt cell is flagged for this criterion */
    isGanttCellFlagged(result, date, criterion) {
      const day = this.getDayData(result, date);
      if (!day) return false;
      return day.matchedCriteria?.includes(criterion.id) || false;
    },

    /** Check if this dateIdx is the start of a Gantt bar segment */
    isGanttBarStart(result, dateIdx, criterion) {
      const cMap = this.ganttBarMap[result.id];
      if (!cMap) return false;
      const segments = cMap[criterion.id];
      if (!segments) return false;
      return segments.some(s => s.startIdx === dateIdx);
    },

    /** Check if this dateIdx falls inside any Gantt bar segment (start to end inclusive) */
    isInGanttBar(result, dateIdx, criterion) {
      const cMap = this.ganttBarMap[result.id];
      if (!cMap) return false;
      const segments = cMap[criterion.id];
      if (!segments) return false;
      return segments.some(s => dateIdx >= s.startIdx && dateIdx <= s.endIdx);
    },

    /** Get inline style for a Gantt bar (width spans across consecutive flex cells) */
    getGanttBarStyle(result, dateIdx, criterion) {
      const cMap = this.ganttBarMap[result.id];
      if (!cMap) return {};
      const segments = cMap[criterion.id];
      if (!segments) return {};
      const seg = segments.find(s => s.startIdx === dateIdx);
      if (!seg) return {};
      const span = seg.endIdx - seg.startIdx + 1;
      // Each cell is flex:1, so span cells = span/total of the row width
      // Use calc() with 100% being one cell's width (the bar's positioned parent)
      return {
        width: `calc(${span * 100}% + ${(span - 1) * 1}px - 6px)`,
        minWidth: `calc(${span * 100}% + ${(span - 1) * 1}px - 6px)`
      };
    },

    /** Get the label for a Gantt bar (uses alert event name for alert criteria) */
    getGanttBarLabel(result, dateIdx, criterion) {
      const cMap = this.ganttBarMap[result.id];
      if (!cMap) return this.criterionLabel(criterion);
      const segments = cMap[criterion.id];
      if (!segments) return this.criterionLabel(criterion);
      const seg = segments.find(s => s.startIdx === dateIdx);
      return seg?.label || this.criterionLabel(criterion);
    }
  }
};
</script>

<style scoped>
.exception-date-grid {
  font-size: 0.85rem;
  position: relative;
}

/* ===== Scroll Wrapper ===== */
.grid-scroll-wrapper {
  overflow: auto;
  border: 1px solid #dee2e6;
  border-radius: 4px;
}

/* ===== Header ===== */
.grid-header {
  display: flex;
  position: sticky;
  top: 0;
  z-index: 3;
  background: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
}

.grid-corner {
  width: 150px;
  min-width: 150px;
  padding: 8px;
  background: #f8f9fa;
  border-right: 2px solid #dee2e6;
  display: flex;
  align-items: flex-end;
  position: sticky;
  left: 0;
  z-index: 4;
}

.sort-location-btn {
  font-size: 0.75rem;
  line-height: 1;
  opacity: 0.6;
  vertical-align: middle;
}
.sort-location-btn:hover {
  opacity: 1;
}

.grid-header-cells {
  display: flex;
  flex: 1 1 0;
  min-width: 0;
}

/* Daily date headers */
.grid-date-header {
  flex: 1 1 0;
  min-width: 50px;
  padding: 4px 2px;
  text-align: center;
  background: #f8f9fa;
  border-right: 1px solid #e9ecef;
}

.grid-date-header.today {
  background: #e7f1ff;
}

.grid-date-header.weekend {
  background: #f1f1f1;
}

.date-dow {
  font-size: 0.7rem;
  color: #6c757d;
  text-transform: uppercase;
}

.date-day {
  font-weight: 600;
  font-size: 0.9rem;
}

.date-month {
  font-size: 0.6rem;
  color: #767676;
}

/* ===== Body ===== */
.grid-body {
}

/* ===== Row Layout ===== */
.grid-row {
  display: flex;
  border-bottom: 1px solid #e9ecef;
}

.grid-row:hover {
  background: #f8f9fa;
}

.grid-location {
  width: 150px;
  min-width: 150px;
  padding: 8px;
  border-right: 2px solid #dee2e6;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  background: #fff;
  position: sticky;
  left: 0;
  z-index: 3;
}

.grid-row:hover .grid-location {
  background: #f8f9fa;
}

.grid-row-element .grid-location {
  background: #f9f9f9;
}

.grid-row-element:hover .grid-location {
  background: #f0f4ff;
}

.location-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 500;
}

.grid-row-cells {
  display: flex;
  flex: 1 1 0;
  min-width: 0;
}

/* ===== Cells ===== */
.grid-cell {
  flex: 1 1 0;
  min-width: 50px;
  padding: 4px 2px;
  text-align: center;
  border-right: 1px solid #e9ecef;
  cursor: pointer;
  transition: background-color 0.15s ease;
  position: relative;
}

.grid-cell:hover {
  background: #e9ecef;
}

.cell-ok {
  background: #d1e7dd;
}

.cell-flagged {
  background: transparent;
}

/* Mini-Gantt: values inside colored bars on unexpanded summary row */
.cell-mini-gantt {
  display: flex;
  flex-direction: column;
  gap: 1px;
  width: 100%;
}

.mini-gantt-row {
  width: 100%;
  height: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.mini-gantt-val {
  font-size: 0.58rem;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
}

.mini-gantt-bar-none {
  background: transparent;
}
.mini-gantt-bar-none .mini-gantt-val {
  color: #6c757d;
  font-weight: 500;
}

.mini-gantt-bar-warning  { background: #ffc107; }
.mini-gantt-bar-warning  .mini-gantt-val { color: #4a3800; }
.mini-gantt-bar-danger   { background: #dc3545; }
.mini-gantt-bar-danger   .mini-gantt-val { color: #fff; }
.mini-gantt-bar-info     { background: #3c76f2; }
.mini-gantt-bar-info     .mini-gantt-val { color: #fff; }
.mini-gantt-bar-purple   { background: #6f42c1; }
.mini-gantt-bar-purple   .mini-gantt-val { color: #fff; }
.mini-gantt-bar-orange   { background: #fd7e14; }
.mini-gantt-bar-orange   .mini-gantt-val { color: #4a2600; }
.mini-gantt-bar-teal     { background: #20c997; }
.mini-gantt-bar-teal     .mini-gantt-val { color: #003d2d; }
.mini-gantt-bar-pink     { background: #d63384; }
.mini-gantt-bar-pink     .mini-gantt-val { color: #fff; }
.mini-gantt-bar-indigo   { background: #6610f2; }
.mini-gantt-bar-indigo   .mini-gantt-val { color: #fff; }
.mini-gantt-bar-cyan     { background: #0891b2; }
.mini-gantt-bar-cyan     .mini-gantt-val { color: #fff; }
.mini-gantt-bar-lime     { background: #84cc16; }
.mini-gantt-bar-lime     .mini-gantt-val { color: #2a4000; }
.mini-gantt-bar-success  { background: #198754; }
.mini-gantt-bar-success  .mini-gantt-val { color: #fff; }



.cell-warning {
  background: #fff3cd;
}

.cell-danger {
  background: #f8d7da;
}

.cell-content {
  position: relative;
  width: 100%;
}



.cell-value {
  font-weight: 600;
}

.cell-secondary {
  font-size: 0.65rem;
  color: #6c757d;
}

.cell-indicator {
  position: absolute;
  top: -2px;
  right: -2px;
  font-size: 0.6rem;
  color: #856404;
}

.cell-empty {
  background: #e9ecef;
  border-radius: 3px;
  min-height: 100%;
}

.cell-loading {
  color: #6c757d;
}

.cell-error {
  color: #dc3545;
  font-weight: bold;
}

.grid-empty {
  padding: 40px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* ===== Legend ===== */
.grid-legend {
  display: flex;
  gap: 16px;
  padding: 8px 0;
  margin-top: 8px;
  border-top: 1px solid #dee2e6;
  font-size: 0.8rem;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.legend-swatch {
  width: 16px;
  height: 16px;
  border-radius: 3px;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.legend-ok {
  background: #d1e7dd;
}

.legend-clear {
  background: #fff;
  border: 1px solid #949494;
}

.legend-flagged {
  background: #fff8e1;
  border: 1px solid #ffe082;
}

.legend-warning {
  background: #fff3cd;
}

.legend-danger {
  background: #f8d7da;
}

/* ===== Element expansion rows ===== */
.grid-row-element {
  background: #f9f9f9;
}

.grid-row-element .grid-cell {
  font-size: 0.72rem;
  padding: 2px 1px;
}

.grid-location-element {
  padding-left: 20px;
  font-size: 0.75rem;
}

.grid-location-element .location-name {
  font-weight: 400;
  font-style: italic;
  display: flex;
  align-items: center;
  gap: 4px;
}

.criterion-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
}
.dot-warning { background: #ffc107; }
.dot-danger { background: #dc3545; }
.dot-info { background: #3c76f2; }
.dot-purple { background: #6f42c1; }
.dot-orange { background: #fd7e14; }
.dot-teal { background: #20c997; }
.dot-pink { background: #d63384; }
.dot-indigo { background: #6610f2; }
.dot-cyan { background: #0891b2; }
.dot-lime { background: #84cc16; }
.dot-success { background: #198754; }

.cell-value-sm {
  font-size: 0.7rem;
  font-weight: 500;
}

.cell-ok-muted {
  background: #f0f8f0;
}

.cell-info {
  background: #d4e0fc;
}
.cell-purple {
  background: #e2d9f3;
}
.cell-orange {
  background: #ffe5cc;
}
.cell-teal {
  background: #d2f4ea;
}
.cell-pink {
  background: #f7d6e6;
}
.cell-indigo {
  background: #e0cffc;
}
.cell-cyan {
  background: #cff4fc;
}
.cell-lime {
  background: #ecfccb;
}
.cell-success {
  background: #d1e7dd;
}

/* ===== Hourly mode ===== */
.grid-hourly-mode .grid-header-cells {
  gap: 0;
}

.grid-date-group {
  display: flex;
  flex-direction: column;
  border-right: 2px solid #dee2e6;
  background: #f8f9fa;
  flex-shrink: 0;
}

.grid-date-group.today {
  background: #e7f1ff;
}

.grid-date-group.weekend {
  background: #f1f1f1;
}

.date-group-label {
  text-align: center;
  font-size: 0.7rem;
  padding: 2px 4px;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  gap: 3px;
  justify-content: center;
  font-weight: 600;
  white-space: nowrap;
}

.hour-headers {
  display: flex;
}

.grid-hour-header {
  width: 34px;
  min-width: 34px;
  text-align: center;
  font-size: 0.65rem;
  color: #6c757d;
  padding: 2px 1px;
  border-right: 1px solid #d5d5d5;
}

.grid-day-cells {
  display: flex;
  flex-shrink: 0;
  border-right: 2px solid #dee2e6;
}

.grid-cell-hourly {
  width: 34px;
  min-width: 34px;
  padding: 4px 1px;
  font-size: 0.7rem;
  border-right: none;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Rounded corners on triggered bar edges */
.grid-cell-hourly.hourly-bar-start {
  border-radius: 4px 0 0 4px;
}
.grid-cell-hourly.hourly-bar-end {
  border-radius: 0 4px 4px 0;
}
.grid-cell-hourly.hourly-bar-start.hourly-bar-end {
  border-radius: 4px;
}

/* Strong backgrounds for triggered hourly cells (match gantt bar colors) */
.grid-cell-hourly.cell-warning { background: #ffc107; }
.grid-cell-hourly.cell-danger  { background: #dc3545; }
.grid-cell-hourly.cell-info    { background: #3c76f2; }
.grid-cell-hourly.cell-purple  { background: #6f42c1; }
.grid-cell-hourly.cell-orange  { background: #fd7e14; }
.grid-cell-hourly.cell-teal    { background: #20c997; }
.grid-cell-hourly.cell-pink    { background: #d63384; }
.grid-cell-hourly.cell-indigo  { background: #6610f2; }
.grid-cell-hourly.cell-cyan    { background: #0891b2; }
.grid-cell-hourly.cell-lime    { background: #84cc16; }
.grid-cell-hourly.cell-success { background: #198754; }

.grid-row-expandable {
  cursor: pointer;
}

.grid-row-expandable:hover {
  background: #f0f4ff;
}

.grid-row-expandable:hover .grid-location {
  background: #f0f4ff;
}

/* ===== Gantt Mode ===== */
.grid-row-gantt {
  min-height: 28px;
}

.gantt-row-cells {
  position: relative;
}

.gantt-cell {
  position: relative;
  overflow: visible;
  border-right-color: rgba(0, 0, 0, 0.04) !important;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gantt-cell-value {
  font-size: 0.7rem;
  font-weight: 600;
  line-height: 1;
  position: relative;
  z-index: 2;
  pointer-events: none;
}

.gantt-cell-value-muted {
  color: #555;
  font-weight: 500;
}

.gantt-cell-value-bar {
  color: #fff;
  text-shadow: 0 1px 2px rgba(0,0,0,0.3);
}

/* Dark text on light-colored bars */
.gantt-value-warning { color: #000; text-shadow: none; }
.gantt-value-danger  { color: #fff; }
.gantt-value-info    { color: #fff; }
.gantt-value-purple  { color: #fff; }
.gantt-value-orange  { color: #000; text-shadow: none; }
.gantt-value-teal    { color: #000; text-shadow: none; }
.gantt-value-pink    { color: #fff; }
.gantt-value-indigo  { color: #fff; }
.gantt-value-cyan    { color: #fff; }
.gantt-value-lime    { color: #000; text-shadow: none; }
.gantt-value-success { color: #fff; }

.gantt-cell-active {
  background: rgba(0, 0, 0, 0.02);
}

.gantt-bar {
  position: absolute;
  top: 2px;
  left: 3px;
  bottom: 2px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  padding: 0 6px;
  z-index: 1;
  white-space: nowrap;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
  pointer-events: none;
}

.gantt-bar-label {
  font-size: 0.6rem;
  font-weight: 600;
  color: #fff;
  text-overflow: ellipsis;
  overflow: hidden;
}

.gantt-bar-warning  { background: #ffc107; }
.gantt-bar-warning .gantt-bar-label { color: #000; }
.gantt-bar-danger   { background: #dc3545; }
.gantt-bar-info     { background: #3c76f2; }
.gantt-bar-info .gantt-bar-label { color: #fff; }
.gantt-bar-purple   { background: #6f42c1; }
.gantt-bar-orange   { background: #fd7e14; }
.gantt-bar-teal     { background: #20c997; }
.gantt-bar-teal .gantt-bar-label { color: #000; }
.gantt-bar-pink     { background: #d63384; }
.gantt-bar-indigo   { background: #6610f2; }
.gantt-bar-cyan     { background: #0891b2; }
.gantt-bar-lime     { background: #84cc16; }
.gantt-bar-lime .gantt-bar-label { color: #000; }
.gantt-bar-success  { background: #198754; }
</style>
